import { textProperties } from '@domain/property';
import { VARIABLE_PREFIX } from '@domain/text';
import { cloneDeep } from '@studio/utils/clone';
import { uuidv4 } from '@studio/utils/id';
import { CRC32checksum, deepEqual, mod, omit } from '@studio/utils/utils';
import { Color } from '../../color';
import { createDefaultTextProperties, isTextNode } from '../../nodes/helpers';
import { convertCharacterStyleToDto } from '../../serialization/character-properties-serializer';
import { deserializeVersionPropertyValue, serializeVersionPropertyValue } from '../../serialization/property-value-serializer';
import { deserializeVersionedText, serializeVersionedText } from '../../serialization/text-serializer';
import { decodeFeedPath } from '../feed/feeds.utils';
import { createWordSpanFromCharacter } from './rich-text.span.utils';
export function isSpaceLikeCharacter(ch) {
    return (ch === 32 /* CharacterCode.Space */ ||
        ch === 9 /* CharacterCode.Tab */ ||
        ch === 11 /* CharacterCode.VerticalTab */ ||
        ch === 12 /* CharacterCode.FormFeed */ ||
        ch === 133 /* CharacterCode.NextLine */ ||
        ch === 5760 /* CharacterCode.Ogham */ ||
        (ch >= 8192 /* CharacterCode.EnQuad */ && ch <= 8203 /* CharacterCode.ZeroWidthSpace */) ||
        ch === 8287 /* CharacterCode.MathematicalSpace */ ||
        ch === 12288 /* CharacterCode.IdeographicSpace */ ||
        ch === 65279 /* CharacterCode.ByteOrderMark */);
}
export function isWordLikeCharacter(ch) {
    return !isSpaceLikeCharacter(ch) && !isNewlineLikeCharacter(ch);
}
export function isStyledSpan(span) {
    return (span.type === "word" /* SpanType.Word */ ||
        span.type === "space" /* SpanType.Space */ ||
        span.type === "variable" /* SpanType.Variable */ ||
        span.type === "newline" /* SpanType.Newline */);
}
function replaceSpaceAndNewLineChars(str) {
    let sanitizedString = '';
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        if (isNewlineLikeCharacter(ch)) {
            sanitizedString += '\n';
            continue;
        }
        if (isSpaceLikeCharacter(ch)) {
            sanitizedString += '\u0020';
            continue;
        }
        sanitizedString += String.fromCharCode(ch);
    }
    return sanitizedString;
}
export function sanitizeText(value) {
    return replaceSpaceAndNewLineChars(value).normalize('NFC').replace(/"/gi, '"');
}
export function isContentProperty(property) {
    return property.name === 'content';
}
export function isVariable(variable, elementOrSpan) {
    if ('kind' in elementOrSpan) {
        if (isTextNode(elementOrSpan)) {
            return true;
        }
        return false;
    }
    return isVariableSpan(elementOrSpan);
}
export function isVariableSpan(span) {
    return span !== undefined && span.type === "variable" /* SpanType.Variable */;
}
export function isContentSpan(span) {
    return (span !== undefined &&
        (span.type === "word" /* SpanType.Word */ ||
            span.type === "space" /* SpanType.Space */ ||
            span.type === "newline" /* SpanType.Newline */ ||
            span.type === "variable" /* SpanType.Variable */));
}
export function isWordSpan(span) {
    return span !== undefined && span.type === "word" /* SpanType.Word */;
}
export function isVersionedVariableContentSpan(span) {
    return span.type === "variable" /* SpanType.Variable */;
}
export function isVersionedTextContentSpan(span) {
    return (span.type === "word" /* SpanType.Word */ ||
        span.type === "space" /* SpanType.Space */ ||
        span.type === "newline" /* SpanType.Newline */ ||
        span.type === "variable" /* SpanType.Variable */);
}
export function isNewlineLikeCharacter(ch) {
    return (ch === 10 /* CharacterCode.LineFeed */ ||
        ch === 13 /* CharacterCode.CarriageReturn */ ||
        ch === 8232 /* CharacterCode.LineSeparator */ ||
        ch === 8233 /* CharacterCode.ParagraphSeparator */ ||
        ch === 133 /* CharacterCode.NextLine */);
}
export function forEachSpan(text, callback) {
    if (text.spans.length > 0) {
        let lastIndex = text.spans.length - 1;
        if (text.spans[lastIndex].type === "end" /* SpanType.End */) {
            lastIndex--;
        }
        for (let i = 0; i < text.spans.length; i++) {
            callback(text.spans[i], i);
        }
    }
}
export function getHorizontalFlexAlignment(horizontalAlignment) {
    switch (horizontalAlignment) {
        case 'left':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'right':
            return 'flex-end';
        case 'justify':
            return 'flex-start';
    }
}
export function getVerticalFlexAlignment(verticalAlignment) {
    switch (verticalAlignment) {
        case 'top':
            return 'flex-start';
        case 'middle':
            return 'center';
        case 'bottom':
            return 'flex-end';
    }
}
export function createRichTextFromString(str) {
    return {
        style: createDefaultTextProperties(),
        spans: createSpansFromString(str).concat([
            {
                type: "end" /* SpanType.End */,
                content: 'END',
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                lineHeight: 0,
                attributes: {},
                dir: "ltr" /* TextDirection.Ltr */
            }
        ])
    };
}
export function createRichTextFromSegments(segments) {
    const spans = createSpansFromSegments(segments);
    return {
        style: createDefaultTextProperties(),
        spans: spans.concat([
            {
                type: "end" /* SpanType.End */,
                content: 'END',
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                lineHeight: 0,
                attributes: {},
                dir: "ltr" /* TextDirection.Ltr */
            }
        ])
    };
}
export function createRichTextVariableFromString(str, attributes = {}, style) {
    return {
        style: createDefaultTextProperties(),
        spans: createSpansFromString(str, attributes, style).concat([
            {
                type: "end" /* SpanType.End */,
                content: 'END',
                top: 0,
                left: 0,
                width: 0,
                height: 0,
                lineHeight: 0,
                attributes: {},
                dir: "ltr" /* TextDirection.Ltr */
            }
        ])
    };
}
export function createSpansFromSegments(segments) {
    const spans = [];
    for (const segment of segments) {
        spans.push(...createSpansFromString(segment.content, undefined, Object.assign({ variable: segment.variable }, segment.value), copyStyle));
    }
    return spans;
}
export function createSpansFromString(str, properties = {}, style, copyStyleFn, styleId, styleIds, previousStyleIds, previousStyleIdToHistoryIndexMap) {
    const spans = [];
    let currentTextSpan;
    let lastSpanType;
    str = typeof str === 'number' ? str.toString() : str;
    /**
     * Make sure variable artifacts are treated as a whole word
     * It's reverted after the spans has been assembled
     * Should only get in here when creating a new feeded element
     */
    if ((style === null || style === void 0 ? void 0 : style.variable) && str === VARIABLE_PREFIX + decodeFeedPath(style.variable.path)) {
        str = str.replace(/\s/g, '&nbsp;');
    }
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        if (isSpaceLikeCharacter(ch)) {
            if (lastSpanType === "space" /* SpanType.Space */) {
                currentTextSpan.content += String.fromCharCode(ch);
            }
            else {
                currentTextSpan = {
                    top: 0,
                    left: 0,
                    type: "space" /* SpanType.Space */,
                    content: String.fromCharCode(ch),
                    attributes: cloneDeep(properties.attributes || {}),
                    style: style ? copyStyleFn(style) : {},
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    styleIds: styleIds || {},
                    styleId,
                    __previousStyleIds: [...(previousStyleIds || [])],
                    __previousStyleIdToHistoryIndexMap: new Map(previousStyleIdToHistoryIndexMap || [])
                };
                spans.push(currentTextSpan);
                lastSpanType = "space" /* SpanType.Space */;
            }
        }
        else if (isNewlineLikeCharacter(ch)) {
            currentTextSpan = {
                top: 0,
                left: 0,
                type: "newline" /* SpanType.Newline */,
                content: String.fromCharCode(ch),
                width: 0,
                height: 0,
                lineHeight: 0,
                style: style ? copyStyleFn(style) : {},
                attributes: cloneDeep(properties.attributes || {}),
                styleIds: styleIds || {},
                styleId,
                __previousStyleIds: [...(previousStyleIds || [])],
                __previousStyleIdToHistoryIndexMap: new Map(previousStyleIdToHistoryIndexMap || [])
            };
            spans.push(currentTextSpan);
            lastSpanType = "newline" /* SpanType.Newline */;
        }
        else {
            if (lastSpanType === "word" /* SpanType.Word */ || lastSpanType === "variable" /* SpanType.Variable */) {
                currentTextSpan.content += String.fromCharCode(ch);
            }
            else {
                if (style === null || style === void 0 ? void 0 : style.variable) {
                    let newStyle = {};
                    newStyle = copyStyleFn ? copyStyleFn(style) : style;
                    currentTextSpan = {
                        type: "variable" /* SpanType.Variable */,
                        content: String.fromCharCode(ch),
                        style: newStyle,
                        top: 0,
                        left: 0,
                        width: 0,
                        height: 0,
                        lineHeight: 0,
                        attributes: cloneDeep(properties.attributes || {}),
                        styleIds: styleIds || {},
                        styleId,
                        __previousStyleIds: [...(previousStyleIds || [])],
                        __previousStyleIdToHistoryIndexMap: new Map(previousStyleIdToHistoryIndexMap || [])
                    };
                }
                else {
                    currentTextSpan = createWordSpanFromCharacter(ch, style !== null && style !== void 0 ? style : {}, properties.attributes, {
                        styleId,
                        styleIds,
                        previousStyleIds,
                        previousStyleIdToHistoryIndexMap
                    });
                }
                spans.push(currentTextSpan);
                lastSpanType = (style === null || style === void 0 ? void 0 : style.variable) ? "variable" /* SpanType.Variable */ : "word" /* SpanType.Word */;
            }
        }
    }
    for (const span of spans) {
        if (isVariableSpan(span)) {
            span.content = span.content.replace(/&nbsp;/gi, ' ');
        }
        span.attributes.shouldRenderNumber = false;
    }
    return spans;
}
export function escapeSpanContent(content) {
    return (content
        .replace(/\n/g, '\\n')
        .replace(/"/g, '\\"')
        .replace(/'/g, `\\'`)
        .replace(/&/g, '\\&')
        .replace(/\r/g, '\\r')
        .replace(/\t/g, '\\t')
        .replace(/\f/g, '\\f')
        .replace(/(\\)(?!n|"|'|&|r|t|f|u\d)/g, '\\\\') // Escape backslashes, if it's not unicode/metachar
        // Legacy: Some documents have newline characters that are not supported. Old Chrome browser cannot handle raw newline characters.
        // They will be converted next time they edit the text. But for now data.js is invalid with these characters.
        .replace(/\u2028/g, '\\u2028') // Line Separator
        .replace(/\u2029/g, '\\u2029') // Paragraph Separator
        .replace(/\u0085/g, '\\u0085')); // Next Line
}
export function unescapeSpanContent(content) {
    return content
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\'/g, "'")
        .replace(/\\&/g, '&')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\f/g, '\f')
        .replace(/\\\\/g, '\\')
        .replace(/\\u2028/g, '\u2028') // Line Separator
        .replace(/\\u2029/g, '\u2029') // Paragraph Separator
        .replace(/\\u0085/g, '\u0085'); // Next Line
}
export function remapStyles(text, oldDocumentId, newDocumentId) {
    if (newDocumentId === oldDocumentId) {
        return text;
    }
    for (const span of text.styles) {
        const oldStyleIds = Object.assign({}, span.styleIds);
        for (const [documentId, styleId] of Object.entries(oldStyleIds)) {
            if (documentId === oldDocumentId) {
                span.styleIds[newDocumentId] = styleId;
                delete span.styleIds[oldDocumentId];
            }
        }
    }
    return text;
}
export function updateStyleIds(property, styleIdMap, newDocumentId) {
    for (const [oldId, newId] of styleIdMap) {
        for (const style of property.value.styles) {
            for (const oldDocumentId in style.styleIds) {
                if (style.styleIds[oldDocumentId] === oldId) {
                    style.styleIds[newDocumentId] = newId;
                    delete style.styleIds[oldDocumentId];
                }
            }
        }
    }
}
export function isVersionedWidgetText(property) {
    const propertyValue = property === null || property === void 0 ? void 0 : property.value;
    if (!propertyValue) {
        return false;
    }
    if (typeof propertyValue === 'string') {
        try {
            const parsed = JSON.parse(propertyValue);
            return parsed.text !== undefined && parsed.styles === undefined;
        }
        catch (_a) {
            return false;
        }
    }
    try {
        return (typeof propertyValue === 'object' &&
            'text' in propertyValue &&
            !('styles' in propertyValue) &&
            propertyValue.text !== undefined);
    }
    catch (_b) {
        return false;
    }
}
export function isVersionedText(property) {
    const propertyValue = property === null || property === void 0 ? void 0 : property.value;
    if (!propertyValue) {
        return false;
    }
    if (typeof propertyValue === 'string') {
        try {
            const parsed = JSON.parse(propertyValue);
            return parsed.text !== undefined && parsed.styles !== undefined;
        }
        catch (_a) {
            return false;
        }
    }
    try {
        return (typeof propertyValue === 'object' &&
            'text' in propertyValue &&
            'styles' in propertyValue &&
            propertyValue.text !== undefined &&
            propertyValue.styles !== undefined);
    }
    catch (_b) {
        return false;
    }
}
/**
 * **NOTE**
 *
 * This function will mutate the provided data node in order to properly re-create character styles
 */
export function cloneNodeStyles(node, documentId) {
    node.__styleHashMap.clear();
    /** `Map<old style id, new style id>` */
    const idMap = new Map();
    for (const [styleId, characterStyle] of [...node.characterStyles]) {
        node.characterStyles.delete(styleId);
        const newStyleId = uuidv4();
        idMap.set(styleId, newStyleId);
        for (const span of node.content.spans) {
            if (isContentSpan(span)) {
                if (span.styleId === styleId) {
                    span.styleId = newStyleId;
                }
                if (span.styleIds[documentId] === styleId) {
                    span.styleIds[documentId] = newStyleId;
                }
            }
        }
        node.__styleHashMap.set(getHashFromStyle(characterStyle), newStyleId);
        node.characterStyles.set(newStyleId, characterStyle);
    }
    return idMap;
}
export function hasSameStyleIds(source, target) {
    if (Object.keys(source).length !== Object.keys(target).length) {
        return false;
    }
    for (const [sourceDesignId, sourceStyleId] of Object.entries(source)) {
        if (!(target[sourceDesignId] && target[sourceDesignId] === sourceStyleId)) {
            return false;
        }
    }
    return true;
}
export function hasSamePreviousStyleIds(source, target) {
    // undefined or empty arrays are the same -- Problem from TP
    if (!(source === null || source === void 0 ? void 0 : source.length) && !(target === null || target === void 0 ? void 0 : target.length)) {
        return true;
    }
    return deepEqual(source, target);
}
export function copyVersionPropertyValue(property) {
    const value = property.value;
    if (isVersionedWidgetText(property)) {
        return cloneDeep(property.value);
    }
    if (isVersionedText(property)) {
        return deserializeVersionedText(serializeVersionedText(property.value));
    }
    return deserializeVersionPropertyValue(property.name, serializeVersionPropertyValue(property.name, value));
}
/**
 * Get raw string from IText (without formatting)
 * @param text
 * @param tw
 */
export function getTextContent(text) {
    let str = '';
    outer: for (const span of text.spans) {
        switch (span.type) {
            case "newline" /* SpanType.Newline */:
                str += '\n';
                break;
            case "space" /* SpanType.Space */:
            case "word" /* SpanType.Word */:
                str += span.content;
                break;
            case "variable" /* SpanType.Variable */:
                str += span.content;
                break;
            case "end" /* SpanType.End */:
                break outer;
            default:
                throw new Error('Unknown span type.');
        }
    }
    return str;
}
export function isSubStyleOf(source, target) {
    const sourceStyle = omit(source, 'variable');
    const targetStyle = omit(target, 'variable');
    if (Object.keys(sourceStyle).length < Object.keys(targetStyle).length) {
        return false;
    }
    for (const property in targetStyle) {
        if (!hasSameStyleProperty(property, targetStyle, sourceStyle)) {
            return false;
        }
    }
    return true;
}
export function hasSameStyle(source, target) {
    const sourceStyle = omit(source, 'variable', '__fontFamilyId');
    const targetStyle = omit(target, 'variable', '__fontFamilyId');
    if (Object.keys(sourceStyle).length !== Object.keys(targetStyle).length) {
        return false;
    }
    for (const property in sourceStyle) {
        if (!hasSameStyleProperty(property, sourceStyle, targetStyle)) {
            return false;
        }
    }
    return true;
}
export function hasSameStyleProperty(property, source, target, treatMissingAsFalse = true) {
    const targetValue = target[property];
    if (targetValue === '$mixed') {
        return false;
    }
    const sourceValue = source[property];
    switch (property) {
        case 'uppercase':
        case 'underline':
        case 'strikethrough':
            // We need to distinguish between 'missing' and 'false' values sometimes. An example, is when we want to promote commonly
            // styled properties across whole text to element styles. A 'uppercase: false' is not the same as 'uppercase: undefined'.
            // If we have in the first span 'uppercase: false' and all the rest of the spans has 'uppercase: undefined'. We don't want
            // to promote 'uppercase: false' to element style.
            if (!treatMissingAsFalse) {
                return sourceValue === targetValue;
            }
            return !!sourceValue === !!targetValue;
        case 'font': {
            const targetStyle = targetValue;
            const sourceStyle = sourceValue;
            if (sourceStyle === targetStyle) {
                return true;
            }
            if (targetStyle === undefined || sourceStyle === undefined) {
                return false;
            }
            return (targetStyle.id === sourceStyle.id &&
                targetStyle.fontFamilyId === sourceStyle.fontFamilyId &&
                targetStyle.style === sourceStyle.style &&
                targetStyle.weight === sourceStyle.weight &&
                targetStyle.src === sourceStyle.src);
        }
        case 'textColor':
            if (sourceValue === targetValue) {
                return true;
            }
            if (sourceValue === undefined || targetValue === undefined) {
                return false;
            }
            if (!(sourceValue instanceof Color && targetValue instanceof Color)) {
                throw new Error(`Neither 'targetValue' nor 'sourceValue' is of type Color.`);
            }
            return sourceValue.toString() === targetValue.toString();
        case 'padding': {
            const targetPadding = targetValue;
            const sourcePadding = sourceValue;
            if (sourcePadding === targetPadding) {
                return true;
            }
            if (targetPadding === undefined || sourcePadding === undefined) {
                return false;
            }
            return (targetPadding.top === sourcePadding.top &&
                targetPadding.bottom === sourcePadding.bottom &&
                targetPadding.left === sourcePadding.left &&
                targetPadding.right === sourcePadding.right);
        }
        case 'textShadows': {
            const sourceShadows = sourceValue;
            const targetShadows = targetValue;
            if (typeof targetShadows !== typeof sourceShadows) {
                return false;
            }
            if (targetShadows === sourceShadows) {
                return true;
            }
            if (targetShadows.length !== sourceShadows.length) {
                return false;
            }
            for (let i = 0; i < sourceShadows.length; i++) {
                const sourceShadow = sourceShadows[i];
                const targetShadow = targetShadows[i];
                const isSame = sourceShadow.blur === targetShadow.blur &&
                    sourceShadow.color === targetShadow.color &&
                    sourceShadow.offsetX === targetShadow.offsetX &&
                    sourceShadow.offsetY === targetShadow.offsetY &&
                    sourceShadow.spread === targetShadow.spread;
                if (!isSame) {
                    return false;
                }
            }
            return true;
        }
        default:
            return sourceValue === targetValue;
    }
}
export function getMaxIndexFromStyleMap(styleIndexMap) {
    let maxIndex = 0;
    for (const index of styleIndexMap.values()) {
        if (index > maxIndex) {
            maxIndex = index;
        }
    }
    return maxIndex;
}
export function createStyleIndexMap(text, existingStyleIndexMap) {
    const styleHashToIndexMap = (existingStyleIndexMap === null || existingStyleIndexMap === void 0 ? void 0 : existingStyleIndexMap.styleHashToIndexMap) || new Map();
    const indexToStyleIdsMap = (existingStyleIndexMap === null || existingStyleIndexMap === void 0 ? void 0 : existingStyleIndexMap.indexToStyleIdsMap) || new Map();
    let currentStyleIndex = existingStyleIndexMap
        ? getMaxIndexFromStyleMap(existingStyleIndexMap.styleHashToIndexMap)
        : 0;
    for (let i = 0; i < text.styles.length; i++) {
        const span = text.styles[i];
        switch (span.type) {
            case "word" /* SpanType.Word */:
            case "space" /* SpanType.Space */:
            case "variable" /* SpanType.Variable */:
                if (Object.keys(span.styleIds).length === 0) {
                    continue;
                }
                let styleIndex;
                const sequencedStyleIds = sequenceStyleIds(span.styleIds);
                styleIndex = styleHashToIndexMap.get(sequencedStyleIds);
                if (styleIndex === undefined) {
                    currentStyleIndex++;
                    styleIndex = currentStyleIndex;
                }
                indexToStyleIdsMap.set(styleIndex, span.styleIds);
                styleHashToIndexMap.set(sequencedStyleIds, styleIndex);
                continue;
        }
    }
    return { styleHashToIndexMap, indexToStyleIdsMap };
}
export function sequenceStyleIds(styleIds) {
    let serializedStyle = '';
    const sortedStyleIds = Array.from(Object.values(styleIds)).sort();
    for (const styleId of sortedStyleIds) {
        serializedStyle += styleId;
    }
    return serializedStyle;
}
export function getHashFromStyle(style) {
    // Filter varaible from ICharacterProperties, it's saved in VersionProperty
    const filteredStyle = omit(style, 'variable');
    return CRC32checksum(sequenceStyle(filteredStyle));
}
export function isEmptyStyle(style) {
    return Object.keys(omit(style, '__fontFamilyId', 'variable')).length === 0;
}
export function getCommonStyledPropertiesFromUniqueOnes(uniquelyStyledProperties, style) {
    return new Set(Object.keys(style).filter(property => !uniquelyStyledProperties.has(property)));
}
export function sequenceStyle(style) {
    let styleSequencedString = '';
    for (const property of textProperties) {
        if (!style) {
            continue;
        }
        const styleValue = style[property];
        if (styleValue === undefined) {
            continue;
        }
        if (property === 'font') {
            styleSequencedString +=
                property +
                    (typeof styleValue === 'string' ? styleValue : styleValue.id);
        }
        else if (styleValue !== undefined) {
            styleSequencedString +=
                property +
                    JSON.stringify(convertCharacterStyleToDto({ [property]: styleValue })[property]);
        }
    }
    return styleSequencedString;
}
export function copySpans(spans) {
    const result = [];
    for (const span of spans) {
        result.push(copySpan(span));
    }
    return result;
}
export function copySpan(span) {
    const copy = cloneDeep(span);
    if (isContentSpan(span)) {
        const contentSpanCopy = copy;
        contentSpanCopy.styleId = span.styleId;
        contentSpanCopy.style = cloneDeep(span.style);
        contentSpanCopy.styleIds = Object.assign({}, (span.styleIds || {}));
        if (span.__previousStyleIdToHistoryIndexMap) {
            contentSpanCopy.__previousStyleIdToHistoryIndexMap = new Map([
                ...span.__previousStyleIdToHistoryIndexMap
            ]);
        }
        contentSpanCopy.__previousStyleIds = cloneDeep(span.__previousStyleIds);
    }
    return copy;
}
export function copyStyle(style) {
    const styleCopy = {};
    for (const property in style) {
        if (style[property] === undefined) {
            continue;
        }
        switch (property) {
            case 'textColor': {
                const prop = style[property];
                if (prop instanceof Color) {
                    styleCopy.textColor = prop.copy();
                }
                break;
            }
            case 'fontSize':
                styleCopy.fontSize = style.fontSize;
                break;
            default:
                styleCopy[property] = cloneDeep(style[property]);
                break;
        }
    }
    return styleCopy;
}
export function resolveElementTextStyle(element) {
    var _a;
    if (isTextNode(element)) {
        const dataNode = element.__data;
        const text = (_a = element.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.editor_m.styleResolver.getResolvedText();
        if (!text) {
            return element;
        }
        for (const property in text.style) {
            if (!textProperties.includes(property)) {
                continue;
            }
            element[property] = element.__data[property] = text.style[property];
        }
        if (dataNode.__dirtyContent) {
            dataNode.__dirtyContent.spans = text.spans;
        }
    }
    return element;
}
const NINETY_DEGREES = Math.PI / 2;
const ONE_EIGHTY_DEGREES = Math.PI;
const TWO_SEVENTY_DEGREES = 1.5 * Math.PI;
export function calculateTop(textLineElement, boundingRect, rotationZ) {
    const rotation = -rotationZ || 0;
    const modRotation = mod(rotation, 2 * Math.PI);
    const lineElementBoundingRect = textLineElement.getBoundingClientRect();
    // first quadrant
    if (modRotation < NINETY_DEGREES) {
        return (lineElementBoundingRect.top - boundingRect.top) / Math.cos(modRotation);
    }
    // ninety degrees
    else if (modRotation === NINETY_DEGREES) {
        return lineElementBoundingRect.left - boundingRect.left;
    }
    // second quadrant
    else if (modRotation < ONE_EIGHTY_DEGREES) {
        return (boundingRect.bottom - lineElementBoundingRect.bottom) / Math.cos(Math.PI - modRotation);
    }
    // one eighty degrees
    else if (modRotation === ONE_EIGHTY_DEGREES) {
        return boundingRect.bottom - lineElementBoundingRect.bottom;
    }
    // third quadrant
    else if (modRotation < TWO_SEVENTY_DEGREES) {
        return (boundingRect.bottom - lineElementBoundingRect.bottom) / Math.cos(modRotation - Math.PI);
    }
    // two seventy degrees
    else if (modRotation === TWO_SEVENTY_DEGREES) {
        return boundingRect.right - lineElementBoundingRect.right;
    }
    // fourth quadrant
    else {
        return ((boundingRect.right - lineElementBoundingRect.right) / Math.cos(modRotation - 1.5 * Math.PI));
    }
}
